import { getProductRating } from '@/scripts/utils/productRating.utils';
import { PRODUCT_USAGE_LABELS } from "@/scripts/constants/productUsageLabels";
import { CameraVideoItemResponseDTO, ProductSlimResponseDTO } from "@shared/DTO"
import { ProductTypeEnum, ProductUsageEnum, SensorType, VideoFormatEnum } from "@shared/enums";
import { FixedLensCameraTypes, InterchangeableLensCameraTypes, LensTypes } from "@shared/values";

type CardSpecItem = {
    label: string,
    icon: string
}


const getRoundedNum = (num: number) => Math.round(num * 10) / 10;

const getApertureSpecItem = (product: ProductSlimResponseDTO): CardSpecItem => {
    const aperture = product?.lensData?.aperture?.max;
    if(!aperture?.length) return undefined;
    const label = aperture
    .filter((a,i,arr) => a > 0 && arr.indexOf(a) === i )
    .map( ap => `f/${ap.toString().replaceAll('.',',')}`)
    .join('-');
    const icon = 'icon-aperture';
    return {label, icon};
}

const getFocalSpecItem = (product: ProductSlimResponseDTO): CardSpecItem => {
    const focal = product?.lensData?.focals?.[0];
    if(!focal) return undefined;
    const { min, max } = focal;
    if(!(min > 0) || !(max >= min)) return undefined;
    const label = min === max ? `${min}mm` : `${min}-${max}mm`;
    const icon = 'icon-focal';
    return {label, icon};
}

const getZoomSpecItem = (product: ProductSlimResponseDTO): CardSpecItem => {
    const focal = product?.lensData?.focals?.[0];
    if(!focal) return undefined;
    const { min, max } = focal;
    if(!(min > 0) || !(max > min)) return undefined;
    const zoom = getRoundedNum(max / min);
    const label = `Zoom ${zoom.toString().replaceAll('.',',')}x`;
    const icon = 'icon-zoom';
    return {label, icon};
}

const getWeightSpecItem = (product: ProductSlimResponseDTO): CardSpecItem => {
    const weight = product?.productData?.weight;
    if(!weight || weight <= 0) return undefined;
    return {
        label: `${weight}g`,
        icon: 'icon-weight'
    };
}

const getSensorSpecItem = (product: ProductSlimResponseDTO): CardSpecItem => {
    const sensorData = product?.cameraData?.sensor;
    const {sensorResolution, sensorType} = sensorData || {};
    
    if(!sensorResolution) return null;

    const sensorTypeText =  [
         SensorType.I_1,
         SensorType.FULL_FRAME,
         SensorType.APS_C,
         SensorType.QUATTRO_TERZI,
         SensorType.MEDIO_FORMATO,
    ].includes(sensorType) ? sensorType : "";

    const sensorResolutionText = `${sensorResolution}MP`;

    const label = `${[sensorTypeText,sensorResolutionText].join(" ")}`;
    const icon = `icon-sensor`;
    return { label, icon };
}

const getBurstSpecItem = (product: ProductSlimResponseDTO): CardSpecItem => {
    const fps = product?.cameraData?.burst?.[0]?.fps;    
    if (!fps) return null;

    return {
        label: `Burst ${fps}fps`,
        icon: 'icon-burst'
    };
}

const getStabilizerSpecItem = (product: ProductSlimResponseDTO): CardSpecItem => {
    const stabilizer = product?.cameraData?.stabilizer ?? product?.lensData?.stabilizer;
    if (!stabilizer?.hasIt) return null;
    const stopsText = stabilizer.stops ? ` ${stabilizer.stops} stop` : '';
    return {
        label: `IS ${stopsText}`,
        icon: 'icon-stabilizer'
    };
}

const getVideoSpecItem = (product: ProductSlimResponseDTO): CardSpecItem => {
    const videoData = product?.cameraData?.video;
    if (!videoData?.length) return null;
    const VIDEO_FORMAT_PRIORITY = [
        VideoFormatEnum.VIDEO_8K,
        VideoFormatEnum.VIDEO_6K,
        VideoFormatEnum.VIDEO_6_2K,
        VideoFormatEnum.VIDEO_C6K,
        VideoFormatEnum.VIDEO_4K,
        VideoFormatEnum.VIDEO_C4K
    ];
    let bestVideo: CameraVideoItemResponseDTO;    
    for (const format of VIDEO_FORMAT_PRIORITY) {
        const found = videoData.find(v => v.format === format);
        if (found) { 
            bestVideo = found; 
            break;
        }
    }
    if(!bestVideo) return undefined;
    const fpsText = bestVideo.fps ? `@${bestVideo.fps}fps` : '';
    return {
        label: `Video ${bestVideo.format.toUpperCase()}${fpsText}`,
        icon: 'icon-video'
    };
}

const getElectronicViewfinderSpecItem = (product: ProductSlimResponseDTO): CardSpecItem => {    
    if (!product?.cameraData?.viewfinder?.available) return null;
    return {
        label: 'Mirino',
        icon: 'icon-viewfinder'
    };
}

const getSpecsItems = (product: ProductSlimResponseDTO) => {
    const productType = product.productData.productType;
    const fns: Array<(product: ProductSlimResponseDTO) => CardSpecItem> = [];
    if(InterchangeableLensCameraTypes.includes(productType)) {
        fns.push(
            ...[
                getSensorSpecItem,
                getBurstSpecItem,
                getStabilizerSpecItem,
                getElectronicViewfinderSpecItem,
            ]
        );
    } else if (FixedLensCameraTypes.includes(productType)) {
        fns.push(
            ...[
                getSensorSpecItem,
                getVideoSpecItem,
                getStabilizerSpecItem,
                getElectronicViewfinderSpecItem,
            ]
        );
    } else if (LensTypes.includes(productType)) {
        fns.push(
            ...[
                getFocalSpecItem,
                getZoomSpecItem,
                getApertureSpecItem,
                getStabilizerSpecItem,
                getWeightSpecItem
            ]
        );
    }
    return fns
        .map( (fn) => fn(product) )
        .filter( (item) => !!item )
        .slice(0, 4)
}


const getProductSubcategory = (
    product: ProductSlimResponseDTO
) => {
    if(product?.productData?.subCategory?.length) {
        return product.productData.subCategory.at(0);
    }
    const productType = product?.productData?.productType;
    if(productType === ProductTypeEnum.REFLEX) return `Fotocamera Reflex`;
    if(productType === ProductTypeEnum.BRIDGE) return `Fotocamera Bridge`;
    if(productType === ProductTypeEnum.MIRRORLESS) return `Fotocamera Mirrorless`;
    if(productType === ProductTypeEnum.COMPATTA) return `Fotocamera Compatta`;
    if(productType === ProductTypeEnum.OBIETTIVO) return `Obiettivo`;
    return '';
}

const getBestUsages = (
    product: ProductSlimResponseDTO
) => {
    const usages = product?.productData?.usage ?? [];
    return [...usages]
        .sort((uA, uB) => uB.score - uA.score)
        .map(({ key }) => key === ProductUsageEnum.ASTRO ? `&nbsp;${PRODUCT_USAGE_LABELS[key]}` : PRODUCT_USAGE_LABELS[key]);
}


export { getSpecsItems, getProductSubcategory, getProductRating, getBestUsages}
